import React, { useState } from "react";
import styled from "styled-components";
import Input from "components/foundation/Input/Input";
import AttachmentItem from "components/foundation/AttachmentItem/AttachmentItem";
import Modal from "components/foundation/Modal/Modal";
import FileDropZone from "components/Common/FileDropZone/FileDropZone";
import CreateDocumentFormField from ".";
import mapFileToAttachmentFileList from "features/upload/services/mapFileToAttachmentFileList";
import {
  DocumentAttachmentFileRequest,
  GetUploadPresignedUrlParams,
} from "apis/data-contracts";
import {
  ATTACHMENT_FILE_SIZE_LIMIT,
  ATTACHMENT_FILE_SIZE_LIMIT_IN_MB,
  MAX_ATTACHMENTS,
} from "config/constant";

interface AttachmentFieldProps {
  idField?: keyof DocumentAttachmentFileRequest;
  attachments?: DocumentAttachmentFileRequest[];
  maxAttachments?: number;
  uploadParams: Omit<GetUploadPresignedUrlParams, "fileName">;
  vertical?: boolean;
  onChangeAttachments?: (attachments: DocumentAttachmentFileRequest[]) => void;
  dropZoneContainerRef?: React.RefObject<HTMLDivElement>;
}

const AttachmentField = ({
  attachments = [],
  maxAttachments = MAX_ATTACHMENTS,
  uploadParams,
  vertical,
  onChangeAttachments,
  dropZoneContainerRef,
}: AttachmentFieldProps) => {
  const [openModal, setOpenModal] = useState(false);

  const onChangeFile = async (files: File[]) => {
    if (!files) {
      return;
    }

    const fileCount = files.length || 0;
    if (attachments.length + fileCount > maxAttachments) {
      alert(`첨부파일은 최대 ${maxAttachments}개까지만 등록 가능합니다.`);
      return;
    }

    const newAttachments: DocumentAttachmentFileRequest[] = [];

    for (const file of files) {
      const hasFileSizeExceeded =
        (file?.size || 0) > ATTACHMENT_FILE_SIZE_LIMIT;

      if (!file || hasFileSizeExceeded) {
        setOpenModal(true);
        return;
      }

      const newAttachment = await mapFileToAttachmentFileList({
        ...uploadParams,
        file,
      });

      if (newAttachment) {
        newAttachments.push(newAttachment);
      }
    }

    if (newAttachments.length > 0) {
      const newAttachmentFileList = [...attachments, ...newAttachments];
      onChangeAttachments?.(newAttachmentFileList);
    }
  };

  const onClickRemoveAttachment = (removeIndex: number) => {
    const remainedAttachmentList = attachments.filter(
      (_, index) => index !== removeIndex,
    );

    onChangeAttachments?.(remainedAttachmentList);
  };

  return (
    <>
      <WrapStyle>
        <CreateDocumentFormField
          label="첨부파일"
          vertical={vertical}
          labelStyle={{
            lineHeight: "4.6rem",
          }}
          bodyStyle={{
            maxWidth: "unset",
            display: "flex",
            flexDirection: "column",
            gap: "3rem",
            overflow: "hidden",
          }}
        >
          {attachments.length > 0 &&
            attachments.map((attachment, index) => (
              <AttachmentItem
                key={`${attachment.fileHash}-${index}`}
                url={attachment.documentFileUrl}
                fileName={attachment.fileName}
                onClickRemove={() => onClickRemoveAttachment(index)}
              />
            ))}

          {attachments.length < maxAttachments && (
            <AttachmentInputStyle>
              <Input
                type="file"
                multiple
                placeholder="클릭 혹은 파일을 화면에 드롭하여 업로드 해주세요."
                accept={uploadParams.allowedExtensions.join(", ")}
                onChange={(event) => {
                  const { files } = event.target;
                  if (!files) {
                    return;
                  }

                  onChangeFile(Array.from(files));
                }}
              />
            </AttachmentInputStyle>
          )}
        </CreateDocumentFormField>
      </WrapStyle>

      <Modal.Root open={openModal} onOpenChange={setOpenModal}>
        <Modal.Body
          title={`${ATTACHMENT_FILE_SIZE_LIMIT_IN_MB}MB 이하 파일만 등록 가능합니다.`}
          confirm
        >
          파일 용량을 확인해 주세요.
        </Modal.Body>
      </Modal.Root>

      <FileDropZone
        onChangeFile={onChangeFile}
        containerRef={dropZoneContainerRef}
      />
    </>
  );
};

export default AttachmentField;

const WrapStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const AttachmentInputStyle = styled.div`
  width: 100%;

  display: flex;
  gap: 1.6rem;

  .file-label {
    height: 4.6rem;
  }

  label {
    margin: 0;
  }

  & > * {
    flex: 1;
  }
`;
